import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { IMyGateDeviceRowDetails } from ".";
import { useCreateMyGateDeviceMutation, useEditMyGateDeviceMutation } from "../../../redux/reducers/ots.reducer";

interface IDeviceDetailsDialogProps {
	open: boolean;
	deviceDetails: IMyGateDeviceRowDetails | null;
	onClose: () => void;
}

const DeviceDetailsDialog: FC<IDeviceDetailsDialogProps> = (props) => {
	const { open, deviceDetails, onClose } = props;

	// APIS
	const [
		createMyGateDevice,
		{
			data: createMyGateDeviceResponse,
			isLoading: createMyGateDeviceLoading,
			isSuccess: createMyGateDeviceSuccess,
			error: createMyGateDeviceError
		}
	] = useCreateMyGateDeviceMutation();

	const [
		editMyGateDevice,
		{
			data: editMyGateDeviceResponse,
			isLoading: editMyGateDeviceLoading,
			isSuccess: editMyGateDeviceSuccess,
			error: editMyGateDeviceError
		}
	] = useEditMyGateDeviceMutation();

	const {
		reset,
		control,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm();

	function handleSubmitForm(data: FieldValues) {
		if (deviceDetails) {
			editMyGateDevice({
				id: deviceDetails.id,
				data: {
					device_id: data.device_key,
					is_entry: Boolean(Number(data.direction))
				}
			});
		} else {
			createMyGateDevice({
				device_id: data.device_key,
				is_entry: Boolean(Number(data.direction))
			});
		}
	}

	useEffect(() => {
		if (open) {
			if (deviceDetails) {
				reset({
					device_key: deviceDetails.device_id,
					direction: deviceDetails.is_entry ? "1" : "0"
				});
			} else {
				reset({
					device_key: "",
					direction: "1"
				});
			}
		}
	}, [deviceDetails, open, reset]);

	useEffect(() => {
		if (createMyGateDeviceSuccess && createMyGateDeviceResponse) {
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createMyGateDeviceSuccess, createMyGateDeviceResponse]);

	useEffect(() => {
		if (editMyGateDeviceSuccess && editMyGateDeviceResponse) {
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editMyGateDeviceSuccess, editMyGateDeviceResponse]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle textAlign="center" classes={{ root: "dialog-title-root primary" }}>
				{deviceDetails ? "Device Details" : "Add Device"}
				<Box className="close-dialog-icon-wrapper">
					<IconButton color="inherit" onClick={onClose}>
						<CloseIcon color="inherit" />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent classes={{ root: "dialog-content-root" }}>
				<Grid container spacing={4} component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="device-key-input"
							error={!!errors.device_key}
							classes={{ root: "ots-input-label-root" }}
						>
							Device Key
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							color="primary"
							id="device-key-input"
							placeholder="Enter the Device key"
							autoComplete="off"
							error={!!errors.device_key}
							helperText={errors.device_key && errors.device_key.message ? errors.device_key.message.toString() : ""}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("device_key", { required: "Device key is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="direction-input"
							error={!!errors.direction}
							classes={{ root: "ots-input-label-root" }}
						>
							Direction
						</InputLabel>

						<FormControl fullWidth>
							<Controller
								name="direction"
								control={control}
								defaultValue="1"
								rules={{ required: "Direction is required" }}
								render={({ field: { value, onChange } }) => (
									<RadioGroup row sx={{ gap: "2rem" }}>
										<FormControlLabel
											value={1}
											control={<Radio />}
											label="Entry"
											checked={Number(value) === 1}
											onChange={(_, checked) => onChange(checked ? 1 : 0)}
										/>

										<FormControlLabel
											value={0}
											control={<Radio />}
											label="Exit"
											checked={Number(value) === 0}
											onChange={(_, checked) => onChange(checked ? 0 : 1)}
										/>
									</RadioGroup>
								)}
							/>

							{errors.direction && errors.direction.message ? (
								<FormHelperText error>{errors.direction.message.toString()}</FormHelperText>
							) : null}
						</FormControl>
					</Grid>

					{createMyGateDeviceError ? (
						<Grid item xs={12}>
							<Typography variant="body2" color="var(--color-error-main)">
								{"data" in createMyGateDeviceError && createMyGateDeviceError.data
									? String(createMyGateDeviceError.data)
									: "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					{editMyGateDeviceError ? (
						<Grid item xs={12}>
							<Typography variant="body2" color="var(--color-error-main)">
								{"data" in editMyGateDeviceError && editMyGateDeviceError.data
									? String(editMyGateDeviceError.data)
									: "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					<Grid item xs={12}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
							<LoadingButton
								disableElevation
								variant="contained"
								color="primary"
								type="submit"
								loading={deviceDetails ? editMyGateDeviceLoading : createMyGateDeviceLoading}
							>
								Save
							</LoadingButton>
						</Box>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default DeviceDetailsDialog;
